import { FunctionComponent, useCallback, useState } from "react";
import { colors } from "@ui-kit/ConfigProvider/ConfigProvider";
import { ShareIcon } from "@icons/ShareIcon";
import { Place } from "@dto/Place";
import { FeedPost } from "@dto/FeedPost";
import { Goal } from "@dto/Goal";
import { Tour } from "@dto/Tour";
import { Event } from "@dto/Event";
import { Select } from "@ui-kit/Select/Select";
import { translate } from "@lib/translate";
import { IconWithBackground } from "@icons/IconWithBackground";
import { createAnalyticsEvent } from "@lib/analytics";
import { eventNames } from "@lib/analyticsEventNames";
import { selectIsKioskEnabled } from "@store/client/clientReducer";
import { useAppSelector } from "@store/store";
import { useTranslation } from "react-i18next";
import { addToastAction } from "@store/toastReducer";
import { useAppDispatch } from "@store/store";
import { ShareEntityModal } from "./ShareEntityModal";

interface ShareEntityButtonProps {
  entity: Place | FeedPost | Goal | Tour | Event;
}

const ShareEntityButton: FunctionComponent<ShareEntityButtonProps> = ({
  entity,
}) => {
  const isKioskEnabled = useAppSelector(selectIsKioskEnabled);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);
  const [view, setView] = useState<"sms" | "email">("email");

  const handleShare = useCallback(
    async (values: ShareOption[]) => {
      const platform = values[0];
      const shareUrl = window.location.href;
      const subject = entity.name;

      switch (platform) {
        case "copy":
          await navigator.clipboard.writeText(shareUrl);
          dispatch(
            addToastAction({
              message: t("toasts.linkCopied"),
              type: "success",
            }),
          );
          break;
        case "email":
          window.open(
            `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(shareUrl)}`,
            "_blank",
            "noopener,noreferrer",
          );
          // setView("email");
          // setIsOpen(true);
          break;
        case "sms":
          setView("sms");
          setIsOpen(true);
          break;
        case "facebook":
          createAnalyticsEvent(eventNames.shareItem, {
            id: entity.id,
            type: entity.type,
            name: entity.name,
            platform: platform,
          });
          window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
              shareUrl,
            )}`,
            "_blank",
            "noopener,noreferrer",
          );
          break;
        case "x":
          createAnalyticsEvent(eventNames.shareItem, {
            id: entity.id,
            type: entity.type,
            name: entity.name,
            platform: platform,
          });
          window.open(
            `https://x.com/intent/tweet?url=${encodeURIComponent(
              shareUrl,
            )}&text=${encodeURIComponent(subject)}`,
            "_blank",
            "noopener,noreferrer",
          );
          break;
      }
    },
    [dispatch, entity, t],
  );

  const shareItemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  };

  type ShareOption = "copy" | "email" | "sms" | "facebook" | "x";

  type ShareOptionItem = {
    label: string;
    value: ShareOption;
    iconPrefix: React.ReactNode;
    style: React.CSSProperties;
  };

  const shareOptionsBase: ShareOptionItem[] = [
    {
      label: translate("share.email") ?? "",
      value: "email",
      iconPrefix: (
        <IconWithBackground iconSize={24} theme="client" svgName="mail" />
      ),
      style: shareItemStyle,
    },
    // TODO: Uncomment to add SMS share option FRAM1-3413
    // {
    //   label: translate("share.sms") ?? "",
    //   value: "sms",
    //   iconPrefix: (
    //     <IconWithBackground iconSize={24} theme="client" svgName="sms" />
    //   ),
    //   style: shareItemStyle,
    // },
  ];

  const shareOptionsCopy: ShareOptionItem[] = [
    {
      label: translate("share.copy") ?? "",
      value: "copy",
      iconPrefix: (
        <IconWithBackground iconSize={24} theme="client" svgName="link" />
      ),
      style: shareItemStyle,
    },
  ];

  const shareOptionsSocial: ShareOptionItem[] = [
    {
      label: translate("share.facebook") ?? "",
      value: "facebook",
      iconPrefix: (
        <IconWithBackground iconSize={24} theme="client" svgName="facebook" />
      ),
      style: shareItemStyle,
    },
    {
      label: translate("share.x") ?? "",
      value: "x",
      iconPrefix: (
        <IconWithBackground iconSize={24} theme="client" svgName="x" />
      ),
      style: shareItemStyle,
    },
  ];

  const shareOptions = isKioskEnabled
    ? shareOptionsBase
    : [...shareOptionsCopy, ...shareOptionsBase, ...shareOptionsSocial];

  // Kiosk mode does not support share options currently
  if (isKioskEnabled) {
    return;
  }

  return (
    <>
      <Select
        items={shareOptions}
        selectedValues={[]}
        onChange={handleShare}
        prefixIcon={
          <ShareIcon size={20} color={colors.neutral700} testID="share-icon" />
        }
      />
      <ShareEntityModal view={view} isOpen={isOpen} onClose={handleClose} />
    </>
  );
};

export { ShareEntityButton };
